/**
 * Heuristically add [Intro], [Verse N], [Pre-Chorus], [Chorus], [Bridge], [Outro] tags
 * to plain lyrics.
 *
 * Works even when the source has no blank lines between sections (e.g. lrclib.net or Whisper).
 * Strategy: sliding-window line-repetition detection with fuzzy Jaccard matching.
 *   1. Normalize each line (lowercase, strip punctuation/parentheticals, collapse spaces).
 *   2. For window sizes 1–6, slide through finding groups of N consecutive normalized
 *      lines that appear ≥2 times (exact OR fuzzy ≥65% word overlap) → Chorus candidate.
 *   3. Pick window with highest occurrence count (prefer larger when tied).
 *   4. Mark every exact/fuzzy match of that window as [Chorus].
 *   5. Short sequences (1–2 lines) immediately before a Chorus → [Pre-Chorus].
 *   6. ≤3 unique lines at very start (before any section) → [Intro].
 *   7. ≤3 unique lines at very end (after all sections) → [Outro].
 *   8. Unique block after 2nd chorus occurrence, past song midpoint → [Bridge].
 *   9. Everything else → [Verse] (single) or [Verse 1]/[Verse 2]/… (multiple).
 *
 * Falls back to blank-line block detection when ≥3 blocks exist (more reliable).
 */
export function addSectionTags(lyrics) {
  const allLines = lyrics.split('\n');
  const nonBlankCount = allLines.filter(l => l.trim()).length;
  if (nonBlankCount < 4) return lyrics; // too short to tag

  const normalizeL = (l) => l.toLowerCase().trim()
    .replace(/\([^)]*\)/g, '') // strip parenthetical variants like "(He reigns)", "(our God)"
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  const normalized = allLines.map(normalizeL);

  // ── Check if there are enough blank-line separators to form real sections ────
  // A trailing-only blank line (e.g. from lrclib) yields just 1 block — not useful.
  // Count blocks by finding lines that start a new non-blank run.
  const blockCount = allLines.filter((l, i) =>
    l.trim() !== '' && (i === 0 || allLines[i - 1].trim() === '')
  ).length;
  if (blockCount >= 3) {
    return addSectionTagsByBlocks(allLines, normalized);
  }
  return addSectionTagsByLineRepetition(allLines, normalized);
}

// ── Shared helpers ────────────────────────────────────────────────────────────

/** Word-bag Jaccard similarity between two normalized strings */
function jaccard(a, b) {
  const wa = new Set(a.split(' ').filter(w => w.length > 0));
  const wb = new Set(b.split(' ').filter(w => w.length > 0));
  if (wa.size === 0 && wb.size === 0) return 1;
  if (wa.size === 0 || wb.size === 0) return 0;
  let inter = 0;
  wa.forEach(w => { if (wb.has(w)) inter++; });
  return inter / (wa.size + wb.size - inter);
}

/** Jaccard similarity between two windows expressed as pipe-joined strings */
function windowJaccard(fpA, fpB) {
  return jaccard(fpA.replace(/\|/g, ' '), fpB.replace(/\|/g, ' '));
}

// ── Block-based detection (when blank lines exist between sections) ───────────

function addSectionTagsByBlocks(allLines, normalized) {
  const blocks = []; // each block: { lines: string[], normLines: string[] }
  let curLines = [], curNorm = [];
  for (let i = 0; i < allLines.length; i++) {
    if (allLines[i].trim() === '') {
      if (curLines.length > 0) { blocks.push({ lines: curLines, normLines: curNorm }); curLines = []; curNorm = []; }
    } else {
      curLines.push(allLines[i]); curNorm.push(normalized[i]);
    }
  }
  if (curLines.length > 0) blocks.push({ lines: curLines, normLines: curNorm });
  if (blocks.length <= 1) return allLines.join('\n');

  const fp = (b) => b.normLines.join('|');
  const fps = blocks.map(fp);
  const fpCount = {};
  fps.forEach(f => { fpCount[f] = (fpCount[f] || 0) + 1; });

  const sorted = Object.entries(fpCount).sort((a, b) => b[1] - a[1]);
  const chorusFp = sorted[0]?.[1] >= 2 ? sorted[0][0] : null;

  const fpLabel = {};
  let verseNum = 0;
  let chorusSeen = false;
  let chorusCount = 0;
  const result = [];

  for (let i = 0; i < blocks.length; i++) {
    const f = fps[i];
    const block = blocks[i];
    let label;
    if (fpLabel[f]) {
      label = fpLabel[f];
    } else if (f === chorusFp) {
      label = '[Chorus]';
    } else {
      const nextF = fps[i + 1];
      const isBeforeChorus = nextF === chorusFp;
      const isLastBlock = i === blocks.length - 1;
      if (!chorusSeen && i === 0 && block.lines.length <= 2) {
        label = '[Intro]';
      } else if (isLastBlock && block.lines.length <= 2 && chorusSeen) {
        label = '[Outro]';
      } else if (isBeforeChorus && block.lines.length <= 2) {
        label = '[Pre-Chorus]';
      } else if (chorusSeen && chorusCount >= 2 && i >= Math.floor(blocks.length / 2) && fpCount[f] === 1 && !isLastBlock) {
        label = '[Bridge]';
      } else {
        verseNum++;
        label = `[Verse ${verseNum}]`;
      }
    }
    if (!fpLabel[f]) fpLabel[f] = label;
    if (label === '[Chorus]') { chorusSeen = true; chorusCount++; }
    result.push(label, ...block.lines, '');
  }

  while (result.length && result[result.length - 1] === '') result.pop();
  // Collapse "Verse 1" → "Verse" if only one verse exists
  const joined = result.join('\n');
  return verseNum === 1 ? joined.replace('[Verse 1]', '[Verse]') : joined;
}

// ── Line-repetition detection (when no blank lines exist) ────────────────────

function addSectionTagsByLineRepetition(allLines, normalized) {
  const n = allLines.length;
  if (n < 4) return allLines.join('\n');

  const FUZZY_THRESHOLD = 0.65;

  // Find the best repeating window (size 1–6) with most occurrences.
  // Use exact match first; also count fuzzy matches against the best exact window.
  let bestWinSize = 0, bestCount = 0, bestFp = null;

  for (let wSize = 1; wSize <= Math.min(6, Math.floor(n / 2)); wSize++) {
    // Build exact counts
    const counts = {};
    for (let i = 0; i <= n - wSize; i++) {
      const fp = normalized.slice(i, i + wSize).join('|');
      if (fp.replace(/[|\s]/g, '').length < 4) continue; // skip near-empty
      counts[fp] = (counts[fp] || 0) + 1;
    }
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    if (!top) continue;

    // Count fuzzy cluster size: how many distinct windows are ≥ FUZZY_THRESHOLD similar
    let fuzzyCount = 0;
    const allFps = Object.keys(counts);
    for (const fp of allFps) {
      if (fp === top[0] || windowJaccard(fp, top[0]) >= FUZZY_THRESHOLD) {
        fuzzyCount++;
      }
    }

    // Use count×size as the score — prefers a 6-line chorus ×4 (score 24) over
    // a 2-line hook ×8 (score 16). This is the key quality improvement.
    const effectiveCount = Math.max(top[1], fuzzyCount);
    const score = effectiveCount * wSize;
    const bestScore = bestCount * bestWinSize;
    if (score > bestScore || (score === bestScore && wSize > bestWinSize)) {
      bestCount = effectiveCount; bestFp = top[0]; bestWinSize = wSize;
    }
  }

  // If no repeating section found, just wrap in [Verse]
  if (bestCount < 2 || !bestFp) {
    return '[Verse]\n' + allLines.join('\n');
  }

  // Find all start positions of chorus windows (exact + fuzzy matches)
  const chorusStarts = new Set();
  for (let i = 0; i <= n - bestWinSize; i++) {
    const fp = normalized.slice(i, i + bestWinSize).join('|');
    if (fp === bestFp || windowJaccard(fp, bestFp) >= FUZZY_THRESHOLD) {
      chorusStarts.add(i);
    }
  }

  // Mark chorus lines
  const lineLabel = new Array(n).fill(null);
  chorusStarts.forEach(start => {
    for (let k = 0; k < bestWinSize; k++) lineLabel[start + k] = 'CHORUS';
  });

  // Find Pre-Chorus: 1–2 non-chorus lines immediately before a chorus start
  chorusStarts.forEach(start => {
    const preEnd = start - 1;
    if (preEnd < 0 || lineLabel[preEnd] === 'CHORUS') return;
    let preStart = preEnd;
    while (preStart > 0 && lineLabel[preStart - 1] === null && preStart > preEnd - 2) preStart--;
    if (preEnd - preStart + 1 <= 2 && (preStart === 0 || lineLabel[preStart - 1] === 'CHORUS' || lineLabel[preStart - 1] === 'PRE_CHORUS')) {
      for (let k = preStart; k <= preEnd; k++) {
        if (lineLabel[k] === null) lineLabel[k] = 'PRE_CHORUS';
      }
    }
  });

  // Gather contiguous segments
  const segments = [];
  let i = 0;
  while (i < n) {
    const lbl = lineLabel[i];
    let j = i;
    while (j < n && lineLabel[j] === lbl) j++;
    segments.push({ start: i, end: j - 1, rawLabel: lbl });
    i = j;
  }

  // Label segments: Intro, Outro, Bridge, Verse
  let verseCount = 0;
  let chorusOccurrences = 0;
  const firstChorusSegIdx = segments.findIndex(s => s.rawLabel === 'CHORUS');
  const lastChorusSegIdx = [...segments].reverse().findIndex(s => s.rawLabel === 'CHORUS');
  const lastChorusIdx = lastChorusSegIdx >= 0 ? segments.length - 1 - lastChorusSegIdx : -1;
  const totalChorusSegs = segments.filter(s => s.rawLabel === 'CHORUS').length;

  // Count non-chorus, non-prechoruss segments to know if we need verse numbers
  const verseCandidates = segments.filter((s, si) => {
    if (s.rawLabel !== null) return false; // chorus/pre-chorus handled separately
    const segLen = s.end - s.start + 1;
    const isFirst = si === 0;
    const isLast = si === segments.length - 1;
    if (isFirst && segLen <= 2) return false; // potential intro (max 2 lines)
    if (isLast && segLen <= 2 && totalChorusSegs > 0) return false; // potential outro
    return true;
  });
  const needsNumbers = verseCandidates.length > 1;

  const bridgeUsed = { v: false };
  const result = [];

  for (let si = 0; si < segments.length; si++) {
    const seg = segments[si];
    const segLen = seg.end - seg.start + 1;
    const isFirst = si === 0;
    const isLast = si === segments.length - 1;

    if (seg.rawLabel === 'CHORUS') {
      chorusOccurrences++;
      result.push('[Chorus]');
    } else if (seg.rawLabel === 'PRE_CHORUS') {
      result.push('[Pre-Chorus]');
    } else {
      // Intro: first segment, ≤2 lines, before any chorus
      if (isFirst && segLen <= 2 && firstChorusSegIdx > 0) {
        result.push('[Intro]');
      // Outro: last segment, ≤2 lines, after all choruses
      } else if (isLast && segLen <= 2 && totalChorusSegs > 0 && si > lastChorusIdx) {
        result.push('[Outro]');
      // Bridge: unique block after 2nd chorus occurrence, past midpoint
      } else if (!bridgeUsed.v && chorusOccurrences >= 2 && si >= Math.floor(segments.length / 2)) {
        result.push('[Bridge]');
        bridgeUsed.v = true;
      } else {
        verseCount++;
        result.push(needsNumbers ? `[Verse ${verseCount}]` : '[Verse]');
      }
    }
    for (let k = seg.start; k <= seg.end; k++) result.push(allLines[k]);
    result.push('');
  }

  while (result.length && result[result.length - 1] === '') result.pop();
  return result.join('\n');
}


/**
 * Add section tags using audio structural analysis results from the server.
 *
 * @param {string[]} lyricLines  - Plain lyric lines (no blank lines required)
 * @param {number[]} boundaries  - Section boundary times in seconds [t0, t1, t2, ...]
 * @param {number[]} clusterIds  - Cluster label per segment interval (len = boundaries.length - 1)
 * @param {Array<{text, start, end}>} whisperLines - Whisper lines with timestamps (optional)
 * @param {Array<{start, end}>} instrumentalRanges - Silent ranges (optional)
 * @returns {string} Lyrics with section tags inserted
 */
export function addSectionTagsFromBoundaries(lyricLines, boundaries, clusterIds, whisperLines = [], instrumentalRanges = []) {
  if (!boundaries || !clusterIds || clusterIds.length < 2) {
    // Fall back to text-based algorithm
    return addSectionTags(lyricLines.join('\n'));
  }

  // ── Step 1: Assign each lyric line to a segment ─────────────────────────
  // If we have Whisper timestamps, use them directly.
  // Otherwise distribute lines evenly across segments.
  const n = lyricLines.length;
  const nSegs = clusterIds.length;
  const lineSegment = new Array(n).fill(0);

  if (whisperLines && whisperLines.length > 0) {
    // Build a map from lyric text → whisper line for timestamp lookup
    const normalize = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 30);
    const wMap = {};
    whisperLines.forEach(wl => { wMap[normalize(wl.text || '')] = wl; });

    for (let i = 0; i < n; i++) {
      const key = normalize(lyricLines[i]);
      const wl = wMap[key];
      if (!wl) continue;
      const midTime = (wl.start + (wl.end || wl.start)) / 2;
      // Find which segment contains this time
      for (let s = 0; s < nSegs; s++) {
        if (midTime >= boundaries[s] && midTime < boundaries[s + 1]) {
          lineSegment[i] = s;
          break;
        }
      }
    }

    // Fill gaps by propagation (lines without timestamps inherit from neighbors)
    for (let i = 1; i < n; i++) {
      if (lineSegment[i] === 0 && lineSegment[i - 1] > 0) lineSegment[i] = lineSegment[i - 1];
    }
    for (let i = n - 2; i >= 0; i--) {
      if (lineSegment[i] === 0 && lineSegment[i + 1] > 0) lineSegment[i] = lineSegment[i + 1];
    }
  } else {
    // Evenly distribute lines across segments
    for (let i = 0; i < n; i++) {
      lineSegment[i] = Math.min(nSegs - 1, Math.floor((i / n) * nSegs));
    }
  }

  // ── Step 2: Determine which cluster is the chorus ────────────────────────
  // Cluster with the highest frequency (most segments) = chorus
  const clusterFreq = {};
  clusterIds.forEach(c => { clusterFreq[c] = (clusterFreq[c] || 0) + 1; });
  const chorusCluster = Object.entries(clusterFreq)
    .sort((a, b) => b[1] - a[1])[0][0];
  const chorusClusterNum = parseInt(chorusCluster, 10);

  // ── Step 3: Build contiguous segment groups from lineSegment ─────────────
  // Find runs of lines in the same segment
  const groups = []; // { segIdx, lines: string[] }
  let curSeg = lineSegment[0];
  let curLines = [];
  for (let i = 0; i < n; i++) {
    if (lineSegment[i] !== curSeg && curLines.length > 0) {
      groups.push({ segIdx: curSeg, lines: curLines });
      curSeg = lineSegment[i];
      curLines = [];
    }
    if (lyricLines[i].trim() && !/^\[/.test(lyricLines[i].trim())) {
      curLines.push(lyricLines[i]);
    }
  }
  if (curLines.length > 0) groups.push({ segIdx: curSeg, lines: curLines });

  // ── Step 4: Label groups ─────────────────────────────────────────────────
  let verseNum = 0;
  let chorusCount = 0;
  let bridgeUsed = false;
  const totalGroups = groups.length;
  const result = [];

  // Check for silent intro via instrumentalRanges
  const hasInstrumentalIntro = instrumentalRanges && instrumentalRanges.some(r => r.start < 5);

  for (let gi = 0; gi < groups.length; gi++) {
    const g = groups[gi];
    const isChorus = clusterIds[g.segIdx] === chorusClusterNum;
    const isFirst = gi === 0;
    const isLast = gi === totalGroups - 1;
    const segDuration = boundaries[g.segIdx + 1] ? boundaries[g.segIdx + 1] - boundaries[g.segIdx] : 0;

    if (isChorus) {
      chorusCount++;
      result.push('[Chorus]');
    } else if (!hasInstrumentalIntro && isFirst && segDuration < 20 && g.lines.length <= 2) {
      // Short non-vocal intro before the chorus
      result.push('[Intro]');
    } else if (isLast && chorusCount > 0 && g.lines.length <= 3 && !isChorus) {
      result.push('[Outro]');
    } else if (!bridgeUsed && chorusCount >= 2 && gi >= Math.floor(totalGroups / 2) && !isLast) {
      result.push('[Bridge]');
      bridgeUsed = true;
    } else {
      verseNum++;
      result.push(verseNum > 1 ? `[Verse ${verseNum}]` : '[Verse 1]');
    }

    g.lines.forEach(l => result.push(l));
    result.push('');
  }

  // Collapse "Verse 1" → "Verse" if there's only one verse
  while (result.length && result[result.length - 1] === '') result.pop();
  let joined = result.join('\n');
  if (verseNum === 1) joined = joined.replace('[Verse 1]', '[Verse]');

  // Prepend [Intro] if silent instrumental intro detected
  if (hasInstrumentalIntro && !/^\s*\[intro\]/i.test(joined)) {
    joined = `[Intro]\n\n${joined}`;
  }

  return joined;
}


/**
 * Given tagged lyrics, a list of instrumental ranges [{start, end}], and the
 * Whisper lines [{text, start, end}], inject [Intro] or [Instrumental] tags
 * for sections where no singing was detected.
 *
 * - A silent range at the very start of the song → prepend [Intro]\n\n
 * - A silent range mid-song that aligns with a gap between Whisper lines → inject [Instrumental]\n\n
 * - Duplicate/redundant tags are not added if the lyrics already start with [Intro]
 */
export function injectInstrumentalTags(lyrics, instrumentalRanges, whisperLines) {
  if (!instrumentalRanges || instrumentalRanges.length === 0) return lyrics;

  let result = lyrics;

  for (const range of instrumentalRanges) {
    const isIntro = range.start < 5; // within first 5 seconds = intro
    const tag = isIntro ? '[Intro]' : '[Instrumental]';

    if (isIntro) {
      // Only prepend [Intro] if lyrics don't already start with it
      if (!/^\s*\[intro\]/i.test(result)) {
        result = `${tag}\n\n${result}`;
      }
    } else {
      // Find the Whisper line whose end is closest to range.start
      // (the silence comes after it)
      if (!whisperLines || whisperLines.length === 0) continue;
      let insertAfterLine = -1;
      let bestDist = Infinity;
      for (let i = 0; i < whisperLines.length; i++) {
        const dist = Math.abs((whisperLines[i].end || 0) - range.start);
        if (dist < bestDist) { bestDist = dist; insertAfterLine = i; }
      }
      if (insertAfterLine < 0 || bestDist > 10) continue; // too far off
      // Find the text of the line before the silence and insert after it
      const lineText = (whisperLines[insertAfterLine].text || '').trim();
      if (!lineText) continue;
      // Escape for regex
      const escaped = lineText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(`(${escaped})`, 'i');
      if (re.test(result) && !result.includes(`\n${tag}`)) {
        result = result.replace(re, `$1\n\n${tag}`);
      }
    }
  }

  return result;
}


